import { Actor } from "../../models/Actor";
import {
  getActorAvatarCssColor,
  getActorAvatarInitial,
  getActorAvatarSoftStyle,
} from "../../helpers/actorAvatar";
import React from "react";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import classNames from "classnames";

export type ActorAvatarSize = "22" | "24" | "28" | "32" | "36" | "40";

interface ActorAvatarProps {
  actor?: Actor;
  size?: ActorAvatarSize;
  /**
   * Kept for existing call sites; every theme now renders the same soft square.
   * (The old interaction theme drew a game-blue ring.)
   */
  theme?: "interaction" | "creator" | "creator-selected";
  /** A slightly stronger tint, so the avatar holds up on a selected (washed blue) row. */
  emphasised?: boolean;
  /** Map and viewport markers stay round; everywhere else the avatar is a square. */
  shape?: "square" | "circle";
  className?: string;
}

const SIZES: Record<ActorAvatarSize, { box: number; font: number; radius: number }> = {
  "22": { box: 22, font: 10, radius: 5 },
  "24": { box: 24, font: 11, radius: 5 },
  "28": { box: 28, font: 12, radius: 6 },
  "32": { box: 32, font: 13, radius: 6 },
  "36": { box: 36, font: 15, radius: 8 },
  "40": { box: 40, font: 16, radius: 8 },
};

/**
 * An actor's avatar: a soft square tinted in the actor's colour with their
 * initial in a darker shade of it (design-studio "C · Soft square avatars").
 * The Player is "P" in Creator blue; no actor shows a neutral person icon.
 */
const ActorAvatar: React.FC<ActorAvatarProps> = ({
  actor,
  size = "36",
  emphasised = false,
  shape = "square",
  className,
}) => {
  const { box, font, radius } = SIZES[size];
  const isPlayer = actor?.id === "player";
  const initial = isPlayer ? "P" : actor ? getActorAvatarInitial(actor.workingName) : "";
  const colorStyle = actor
    ? getActorAvatarSoftStyle(getActorAvatarCssColor(actor), emphasised)
    : { backgroundColor: "var(--pi-surface-hover)", color: "var(--pi-menu-text-color-1)" };

  return (
    <div
      data-testid="actor-avatar"
      aria-hidden="true"
      className={classNames(
        "flex flex-none my-auto box-border items-center justify-center font-semibold select-none",
        className
      )}
      style={{
        width: box,
        height: box,
        minWidth: box,
        minHeight: box,
        fontSize: actor ? font : Math.round(box * 0.6),
        borderRadius: shape === "circle" ? "50%" : radius,
        ...colorStyle,
      }}
    >
      {actor === undefined ? (
        <PersonOutlinedIcon color="inherit" fontSize="inherit" />
      ) : (
        initial
      )}
    </div>
  );
};

export default ActorAvatar;
